import type { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as bedrockAgent from '@aws-sdk/client-bedrock-agent-runtime'
import type * as bedrockRuntime from '@aws-sdk/client-bedrock-runtime'

import * as common from '../common'

import type { Conversation } from './conversation'
import * as file from './file'
import { Info } from './info'

export interface Message {
  id: PartialId
  /** @see {@link Message.Role} */
  role: Message.Role
  /** @see {@link Conversation.id}  */
  conversationId: Conversation.Id
  /**
   * The datetime when the message was created.
   */
  createdAt: Date
  /** @see {@link Message.Status} */
  status: Message.Status
}
export namespace Message {
  /**
   * A message together with its entries, in the order they were produced.
   *
   * What a reader needs to show a message, or to replay one as agent history.
   */
  export interface WithInfo extends Message {
    /** @see {@link Info} */
    info: readonly Info[]
  }

  /** Whether the message can be produced again under the same id. */
  export function isRetryable(message: Message): boolean {
    return message.role === 'agent' && message.status === 'failed'
  }

  /**
   * Events emitted while an agent message is being produced.
   *
   * Each update carries the entry it contributes and where that entry goes.
   */
  export type Event = Event.Pending | Event.Completed | Event.Failed
  export namespace Event {
    export interface Pending {
      /** Generated file bytes that must be persisted by the consumer. */
      file?: File.Output
      /** The entry this update contributes. */
      entry: Info
      /** Place in the message's list that the entry takes. */
      seq: number
      type: 'pending'
    }

    export interface Completed {
      type: 'completed'
    }

    export interface Failed {
      error: unknown
      type: 'failed'
    }
  }

  export type Role = 'user' | 'agent'
  export type File = string
  export namespace File {
    /** File produced during an agent-message invocation. */
    export interface Output extends bedrockAgent.OutputFile {
      readonly bytes: Uint8Array
      readonly name: string
    }

    /** Returns a filename that does not collide with an existing message file. */
    export function uniqueName(
      fileName: string,
      existingFiles: readonly string[],
    ): string {
      if (!existingFiles.includes(fileName)) {
        return fileName
      }

      const lastDot = fileName.lastIndexOf('.')
      const base = lastDot !== -1 ? fileName.slice(0, lastDot) : fileName
      const extension = lastDot !== -1 ? fileName.slice(lastDot) : ''
      let counter = 1

      while (existingFiles.includes(`${base} (${counter})${extension}`)) {
        counter += 1
      }
      return `${base} (${counter})${extension}`
    }

    /**
     * Generates a {@link common.fileManagement.Key.Parts.qualifier qualifier}
     * for a this {@link Message} file.
     */
    export function generateQualifier(
      params: generateQualifier.Params,
    ): string {
      const { message, conversation } = params
      const qualifier = `${TypeChecker.isObject(conversation) ? conversation.id : conversation}_${TypeChecker.isObject(message) ? message.id : message}`
      // truncate qualifier characters that are not safe for urls or file systems
      const safeQualifier = qualifier.replace(/[^a-zA-Z0-9-_]/g, '')
      return safeQualifier
    }
    export namespace generateQualifier {
      export interface Params {
        /** {@link Message} or its {@link Message.id} */
        message: Message | Message['id']
        /** {@link Conversation} or its {@link Conversation.id} */
        conversation: Conversation | Conversation['id']
      }
    }

    /**
     * Builds S3 file references for agent input.
     *
     * @example
     * const refs = Message.File.toS3References({
     *   context,
     *   files: ['report.xlsx'],
     *   qualifier: { conversation: 'conv-123', message: 'msg-456' },
     *   bucketName: 'my-bucket',
     * })
     */
    export function toS3References(
      params: toS3References.Params,
    ): file.S3Reference[] {
      const { context, files, qualifier, bucketName } = params
      const resolvedQualifier = generateQualifier(qualifier)
      return files.map(fileName =>
        file.buildS3Reference({
          bucketName,
          context,
          fileName,
          qualifier: resolvedQualifier,
        }),
      )
    }
    export namespace toS3References {
      export interface Params {
        /** Context for key generation. */
        context: common.fileManagement.Key.Parts.Context
        /** File names to build references for. */
        files: readonly string[]
        /** Qualifier params for the S3 key. */
        qualifier: generateQualifier.Params
        /** S3 bucket name. */
        bucketName: string
      }
    }

    /**
     * Creates a file management key input for an agent-generated file.
     *
     * @example
     * const keyInput = Message.File.toKeyInput({
     *   context,
     *   file: { name: 'report.xlsx', bytes: ... },
     *   qualifier: { conversation: 'conv-123', message: 'msg-456' },
     * })
     */
    export function toKeyInput(
      params: toKeyInput.Params,
    ): common.fileManagement.Key {
      const { context, fileName, qualifier } = params

      // Parse filename to extract name and extension
      const lastDotIndex = fileName?.lastIndexOf('.') ?? -1
      const id =
        lastDotIndex !== -1
          ? fileName?.substring(0, lastDotIndex)
          : (fileName ?? 'unnamed')
      const extension =
        lastDotIndex !== -1 ? fileName?.substring(lastDotIndex + 1) : undefined

      return common.fileManagement.Key.from({
        context: file.stripUserId(context),
        extension,
        id,
        purpose: common.fileManagement.Purpose.FileManagement,
        qualifier: generateQualifier(qualifier),
      })
    }
    export namespace toKeyInput {
      export interface Params {
        /** Context for key generation. */
        context: common.fileManagement.Key.Parts.Context
        /** The file to generate the key for. */
        fileName?: string
        /** Qualifier params for the S3 key. */
        qualifier: generateQualifier.Params
      }
    }
  }

  /**
   * The status of the message processing.
   *
   * Usually used to track the state of messages generated by agents.
   */
  export type Status = 'pending' | 'completed' | 'failed'

  export type Prompt = string
  export namespace Prompt {
    /**
     * Convert a {@link Message} to a {@link Prompt} so it can be sent to an agent.
     *
     * The prompt is the concatenation of every `text`-typed entry, in
     * order. Dynamic context (DMS records, user-defined guidance) is composed
     * into the agent's per-invocation `instruction` field upstream, not into
     * the user prompt.
     */
    export function from(info: readonly Info[]): Prompt {
      return Info.withoutSupersededText(info)
        .filter((entry): entry is Info.Text => entry.type === 'text')
        .map(entry => entry.text)
        .join('')
    }
  }

  /** Converts a domain message into agent conversation-history input. */
  export function toBedrockMessage(
    message: WithInfo,
  ): bedrockRuntime.Message & bedrockAgent.Message {
    return {
      content: [{ text: Prompt.from(message.info) }],
      role: message.role === 'user' ? 'user' : 'assistant',
    }
  }

  /**
   *
   * Represents the essential context information for a conversation.
   * Contains the agent and workspace identifiers along with the conversation
   * ID, providing the minimal set of data needed to identify and operate within
   * a specific conversation.
   *
   * @example
   * ```typescript
   * const context: ConversationContext = {
   *   agentId: 'agent-123',
   *   workspaceId: 'ws-456',
   *   conversationId: 'conv-789'
   * };
   * ```
   */
  export type ConversationContext = Partial.Semi<
    Pick<Conversation, 'agentId' | 'workspaceId'>,
    'workspaceId'
  > & {
    /**
     * The ID of the conversation this context belongs to.
     *
     * @see {@link Conversation.Id}
     */
    conversationId: Conversation.Id
  }
}
